import { useCallback, useEffect, useMemo, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";

/**
 * Subscribe to a company's custom roles subcollection.
 *
 * Path: companies/{companyId}/settings/roles/custom (PRD_UAM_CustomRoles.md §6.1)
 * Capped at 5 docs per company so the snapshot stays small.
 *
 * @param {string|undefined} companyId
 * @return {{
 *   roles: Array,
 *   loading: boolean,
 *   error: Error|null,
 *   resolveRoleName: (customRoleId: string|null|undefined) => string,
 *   removeOptimistically: (roleId: string) => void,
 * }}
 *   - roles: array of `{ id, name, description, permissions, createdBy, ... }`
 *   - resolveRoleName: returns the role's display name, or the literal
 *     string "Unknown role" if the ID is missing or points to a deleted
 *     doc. See PRD §5.7 Unknown Role Fallback.
 *   - removeOptimistically: drop a role from local state immediately
 *     without waiting for the next onSnapshot fire. Used to mask the
 *     occasional Firestore SDK BloomFilter recovery delay after a
 *     successful `deleteCustomRole` CF response. The next snapshot is
 *     authoritative — if the role somehow still exists server-side, it
 *     will reappear on the next fire.
 */
export function useRoles(companyId) {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!companyId) {
      setRoles([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const ref = collection(
      db,
      "companies",
      companyId,
      "settings",
      "roles",
      "custom",
    );
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        setRoles(list);
        setLoading(false);
        setError(null);
      },
      (err) => {
        console.error("useRoles snapshot failed:", err);
        setError(err);
        setLoading(false);
      },
    );
    return unsub;
  }, [companyId]);

  const rolesById = useMemo(() => {
    const map = new Map();
    roles.forEach((r) => map.set(r.id, r));
    return map;
  }, [roles]);

  const resolveRoleName = useCallback(
    (customRoleId) => {
      if (!customRoleId) return "Unknown role";
      const role = rolesById.get(customRoleId);
      return role ? role.name || "Unknown role" : "Unknown role";
    },
    [rolesById],
  );

  const removeOptimistically = useCallback((roleId) => {
    setRoles((prev) => prev.filter((r) => r.id !== roleId));
  }, []);

  return { roles, loading, error, resolveRoleName, removeOptimistically };
}
